import DeviceInfo from 'react-native-device-info';
import { omit } from 'lodash-es';
import Config from '@betty/toolkit/config';
import { store } from '@betty/toolkit/store';
import { AppDispatch } from '@betty/toolkit/state';
import { logInfo } from '@betty/toolkit/logging';
import { MINUTE_IN_MILLISECONDS } from '@betty/toolkit/utils';
import { selectAccount } from '@betty/account/state';
import { selectCoinBalance } from '@betty/wallet/state';
import { selectStatus } from '@betty/status/state';
import {
  addEvent,
  addThread,
  threadsLoaded,
  loggedIn,
  setAgent,
  setChat,
  setTyping,
  formReceived,
  setHasUnseenMessages,
  setSessionFieldsTS,
  chatDeactivated,
  reconnectChat,
  setEventProperties,
  selectAuth,
  selectDynamicConfig,
  renewSession,
} from './chat.slice';
import {
  ChatMessage,
  FormMessage,
  AgentMessage,
  EventMessage,
  ThreadsMessage,
  CustomerMessage,
  CustomerUpdatedMessage,
  EventPropertiesUpdatedMessage,
  IncomingTypingIndicatorMessage,
  CustomerDisconnectedMessage,
  DisconnectReason,
  Localization,
  Chat,
  Agent,
  FormField,
  Config as ConfigType,
} from './types';
import { ChatAction } from './chat.types';
import { CHAT_API_URL, HELP_DESK_API_URL } from './chat.config';

const parseChat = (data: ChatMessage) => ({
  ...data,
  users: data.users.reduce(
    (accumulator, user) => ({
      ...accumulator,
      [user.id]: user,
    }),
    {},
  ),
});

const parseForm = (data: FormMessage) => {
  const { form, enabled } = data;

  if (!enabled) {
    return;
  }

  return form;
};

const parseAgent = (data: AgentMessage) => {
  const { agent, queue } = data;

  return {
    ...agent,
    queue: queue,
  };
};

const parseCustomer = (data: CustomerMessage) => {
  const { customer_id, chats, customer } = data;

  return { customer_id, chats, customer };
};

const shouldGenerateAccessToken = (reason: DisconnectReason) =>
  reason === 'access_token_revoked' || reason === 'access_token_expired';

const shouldReconnect = (reason: DisconnectReason) =>
  reason === 'inactivity_timeout' ||
  reason === 'connection_evicted' ||
  reason === 'connection_timeout' ||
  reason === 'service_temporarily_unavailable' ||
  reason === 'too_many_unauthorized_connections' ||
  reason === 'internal_error';

export const getHandlers = (dispatch: AppDispatch) => ({
  [ChatAction.LOGIN]: (data: CustomerMessage) => {
    const chat = data.chats[data.chats.length - 1];

    dispatch(loggedIn(parseCustomer(data)));

    if (chat) {
      dispatch(setHasUnseenMessages(chat.has_unread_events));
    }
  },
  [ChatAction.GET_CHAT]: (data: ChatMessage) => {
    dispatch(setChat(parseChat(data)));
  },
  [ChatAction.LIST_THREADS]: (data: ThreadsMessage) => {
    dispatch(threadsLoaded(data));
  },
  [ChatAction.GET_PREDICTED_AGENT]: (data: AgentMessage) => {
    dispatch(setAgent(parseAgent(data)));
  },
  [ChatAction.GET_FORM]: (data: FormMessage) => {
    dispatch(formReceived(parseForm(data)));
  },
  [ChatAction.INCOMING_CHAT]: (data: { chat: ChatMessage }) => {
    dispatch(setChat(parseChat(data.chat)));
    dispatch(addThread(data.chat.thread));
  },
  [ChatAction.INCOMING_EVENT]: (data: EventMessage) => {
    dispatch(addEvent(data));
    dispatch(setHasUnseenMessages(true));
  },
  [ChatAction.EVENT_PROPERTIES_UPDATED]: (
    data: EventPropertiesUpdatedMessage,
  ) => {
    dispatch(setEventProperties(data));
  },
  [ChatAction.INCOMING_TYPING_INDICATOR]: (
    data: IncomingTypingIndicatorMessage,
  ) => {
    dispatch(setTyping(data));
  },
  [ChatAction.CUSTOMER_UPDATED]: (data: CustomerUpdatedMessage) => {
    if (data.session_fields) {
      dispatch(setSessionFieldsTS());
    }
  },
  [ChatAction.CUSTOMER_DISCONNECTED]: (data: CustomerDisconnectedMessage) => {
    logInfo('Chat', 'disconnected error', data);

    if (shouldGenerateAccessToken(data.reason)) {
      dispatch(renewSession());

      return;
    }

    if (shouldReconnect(data.reason)) {
      dispatch(reconnectChat());
    }
  },
  [ChatAction.CHAT_DEACTIVATED]: () => {
    dispatch(chatDeactivated());
  },
});

export const getAppInfo = (currentScreen = '') => {
  const info = {
    mobileApp: true,
    appVersion: DeviceInfo.getVersion(),
    platform: DeviceInfo.getSystemName(),
    platformVersion: DeviceInfo.getSystemVersion(),
    device: DeviceInfo.getModel(),
    currentScreen,
  };
  const queries = Object.entries(info)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return `${Config.PLATFORM_URL}/${queries}`;
};

export const shouldSetSessionVariables = (sessionFieldsTS?: number) =>
  !sessionFieldsTS ||
  Date.now() - sessionFieldsTS >= MINUTE_IN_MILLISECONDS * 2;

export const sendChatAPIRequest = async ({
  url,
  body,
  method,
  params = {},
}: {
  url: string;
  method: 'GET' | 'POST';
  body?: RequestInit['body'];
  params?: Record<string, string | number>;
}) => {
  const { accessToken } = selectAuth(store.getState()) || {};
  const { organization_id } = selectDynamicConfig(store.getState()) || {};

  if (organization_id) {
    params.organization_id = organization_id;
  }

  const queryParams = Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join('&');

  const response = await fetch(
    `${CHAT_API_URL}${url}${queryParams && `?${queryParams}`}`,
    {
      body,
      method,
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );

  if (!response.ok) {
    const error = await response.text();

    throw new Error(
      `Chat request failed with status ${response.status}: ${error}`,
    );
  }

  return response;
};

export const sendHelpdeskAPIRequest = async ({
  url,
  body,
  method,
  params = {},
}: {
  url: string;
  method: 'GET' | 'POST';
  body?: RequestInit['body'];
  params?: Record<string, string | number>;
}) => {
  const queryParams = Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join('&');

  const response = await fetch(
    `${HELP_DESK_API_URL}${url}${queryParams && `?${queryParams}`}`,
    {
      body,
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    const error = await response.text();

    throw new Error(
      `HelpDesk request failed with status ${response.status}: ${error}`,
    );
  }

  return response;
};

const HTML_ENTITIES: { [key: string]: string } = {
  '&#38;': '&',
  '&#60;': '<',
  '&#62;': '>',
  '&#34;': '"',
  '&#39;': `'`,
  '&#160;': ' ',
  '&#169;': '©',
  '&#174;': '®',
  '&#8482;': '™',
};

const HTML_ENTITIES_PATTERN = new RegExp(
  Object.keys(HTML_ENTITIES).join('|'),
  'gm',
);

export const parseLocalization = (localization: Localization) =>
  Object.keys(localization).reduce(
    (accumulator, key) => ({
      ...accumulator,
      [key]: localization[key as keyof Localization]
        .replaceAll(HTML_ENTITIES_PATTERN, (match) => HTML_ENTITIES[match])
        .replaceAll(/%(.*?)%/gm, '{{$1}}'),
    }),
    {},
  );

export const findChatAgent = (chat?: Chat, agent?: Agent) =>
  Object.values(chat?.users || []).find(
    (user) => user.type === 'agent' && user.present,
  ) ?? agent;

export const parseFormField = ({
  type,
  options,
  id,
  label,
  answer,
}: FormField) => {
  let parsedAnswer;
  if (type === 'radio' && options) {
    const option = options.find((item) => item.checked);
    parsedAnswer = omit(option, 'checked');
  } else {
    parsedAnswer = answer;
  }

  return {
    id,
    label,
    type,
    answer: parsedAnswer,
  };
};

export const getHelpDeskLicenseId = ({ properties }: ConfigType) => {
  const { license } = properties;
  const { hdLicenseID } =
    Object.values(license).find((object) => 'hdLicenseID' in object) || {};

  if (!hdLicenseID) {
    throw new Error('Helpdesk license id is missing');
  }

  return Number(hdLicenseID);
};

export const getSessionVariables = () => {
  const state = store.getState();
  const auth = selectAuth(state);
  const account = selectAccount(state);
  const coinBalance = selectCoinBalance(state);
  const status = selectStatus(state);

  return {
    liveChatCustomerId: auth?.entityId,
    accountStatus: account?.status,
    accountStatusReason: account?.statusReason,
    registrationEmail: account?.email,
    coinBalance,
    playerStatus: status?.playerStatus,
  };
};
